import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cuentasBancariasAPI, bancosAPI, formasPagoAPI, usuariosAPI } from '../services/api';
import { toast } from 'react-toastify';
import UsuariosAccesoSelector from '../components/UsuariosAccesoSelector';
import '../Clientes.css';

const CuentaBancariaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bancos, setBancos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [formData, setFormData] = useState({
    numero_cuenta: '',
    tipo: 'CtaCte',
    banco_id: '',
    moneda: 'ARS',
    cbu_cvu: '',
    alias: '',
    saldo_inicial: '',
    monto_maximo_salida: '',
    activo: true,
    usuario_ids: [],
  });

  const loadBancos = useCallback(async () => {
    try {
      const response = await bancosAPI.getAll({ activo: true });
      if (response.success) {
        setBancos(response.data);
      }
    } catch (error) {
      toast.error('Error al cargar bancos');
    }
  }, []);

  const loadUsuarios = useCallback(async () => {
    try {
      const res = await usuariosAPI.getActivos();
      if (res.success && Array.isArray(res.data)) setUsuarios(res.data);
    } catch {
      setUsuarios([]);
    }
  }, []);

  const loadCuenta = useCallback(async () => {
    try {
      const response = await cuentasBancariasAPI.getById(id);
      if (response.success) {
        const d = response.data;
        setFormData({
          numero_cuenta: d.numero_cuenta || '',
          tipo: d.tipo || 'CtaCte',
          banco_id: d.banco_id != null ? String(d.banco_id) : '',
          moneda: d.moneda || 'ARS',
          cbu_cvu: d.cbu_cvu || '',
          alias: d.alias || '',
          saldo_inicial: d.saldo_inicial != null ? d.saldo_inicial : '',
          monto_maximo_salida: d.monto_maximo_salida != null ? d.monto_maximo_salida : '',
          activo: Boolean(d.activo),
          usuario_ids: Array.isArray(d.usuario_ids) ? d.usuario_ids.map(String) : [],
        });
      }
    } catch (error) {
      toast.error('Error al cargar cuenta bancaria');
      navigate('/cuentas-bancarias');
    }
  }, [id, navigate]);

  useEffect(() => {
    loadBancos();
    loadUsuarios();
    if (id) {
      loadCuenta();
    }
  }, [id, loadBancos, loadCuenta, loadUsuarios]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      banco_id: parseInt(formData.banco_id, 10),
      saldo_inicial: formData.saldo_inicial === '' ? 0 : parseFloat(formData.saldo_inicial),
      monto_maximo_salida: formData.monto_maximo_salida === '' ? null : parseFloat(formData.monto_maximo_salida),
      usuario_ids: formData.usuario_ids.map((x) => parseInt(x, 10)).filter((n) => !isNaN(n)),
    };

    if (id && (formData.activo === false || formData.activo === 0)) {
      try {
        const resFormas = await formasPagoAPI.getAll({ cuenta_bancaria_id: id });
        const formas = (resFormas.success && resFormas.data) ? resFormas.data : [];
        if (formas.length > 0) {
          const nombres = formas.map(fp => fp.nombre).join(', ');
          const confirmar = window.confirm(
            `Al desactivar esta cuenta bancaria también se desactivarán las siguientes formas de pago: ${nombres}.\n\n¿Desea continuar?`
          );
          if (!confirmar) return;
        }
      } catch (err) {
        toast.error('No se pudo verificar las formas de pago asociadas.');
        return;
      }
    }

    setLoading(true);
    try {
      let response;
      if (id) {
        response = await cuentasBancariasAPI.update(id, payload);
      } else {
        response = await cuentasBancariasAPI.create(payload);
      }

      if (response.success) {
        toast.success(id ? 'Cuenta bancaria actualizada exitosamente' : 'Cuenta bancaria creada exitosamente');
        const formasPago = response.formas_pago_que_la_utilizan;
        if (id && formData.activo === false && formasPago && formasPago.length > 0) {
          const nombres = formasPago.map(fp => fp.nombre).join(', ');
          toast.info(`Se desactivaron también las formas de pago: ${nombres}.`, { autoClose: 6000 });
        }
        navigate('/cuentas-bancarias');
      }
    } catch (error) {
      toast.error(error.message || 'Error al guardar cuenta bancaria');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cliente-form">
      <h1>{id ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}</h1>
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Número de Cuenta *</label>
            <input
              type="text"
              name="numero_cuenta"
              value={formData.numero_cuenta}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Tipo *</label>
            <select
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              required
            >
              <option value="CtaCte">Cuenta Corriente</option>
              <option value="CajaAhorros">Caja de Ahorros</option>
            </select>
          </div>

          <div className="form-group">
            <label>Banco *</label>
            <select
              name="banco_id"
              value={formData.banco_id}
              onChange={handleChange}
              required
            >
              <option value="">Seleccione un banco</option>
              {bancos.map(banco => (
                <option key={banco.id} value={banco.id}>{banco.nombre}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Moneda *</label>
            <select
              name="moneda"
              value={formData.moneda || 'ARS'}
              onChange={handleChange}
              required
            >
              <option value="ARS">ARS - Peso argentino</option>
              <option value="USD">USD - Dólar estadounidense</option>
              <option value="EUR">EUR - Euro</option>
            </select>
          </div>

          <div className="form-group">
            <label>Saldo inicial</label>
            <input
              type="number"
              name="saldo_inicial"
              value={formData.saldo_inicial}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label>Monto máximo por egreso</label>
            <input
              type="number"
              name="monto_maximo_salida"
              value={formData.monto_maximo_salida}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="Sin límite"
            />
            <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
              Tope por operación de salida desde esta cuenta. Vacío = sin límite.
            </small>
          </div>

          <UsuariosAccesoSelector
            usuarios={usuarios}
            selectedIds={formData.usuario_ids}
            onChange={(usuario_ids) => setFormData((prev) => ({ ...prev, usuario_ids }))}
            hintText="Si no asigna usuarios, solo el administrador podrá operar esta cuenta."
          />

          <div className="form-group">
            <label>CBU / CVU</label>
            <input
              type="text"
              name="cbu_cvu"
              value={formData.cbu_cvu || ''}
              onChange={handleChange}
              placeholder="22 dígitos"
              maxLength={22}
            />
          </div>

          <div className="form-group">
            <label>Alias</label>
            <input
              type="text"
              name="alias"
              value={formData.alias || ''}
              onChange={handleChange}
              placeholder="Ej: mi.cuenta.banco"
            />
          </div>

          <div className="form-group">
            <label className="form-toggle-wrap">
              <span className="toggle-switch-box">
                <input
                  type="checkbox"
                  name="activo"
                  checked={formData.activo}
                  onChange={handleChange}
                />
                <span className="form-toggle-switch" />
              </span>
              <span className="form-toggle-label">{formData.activo ? 'Desactivar' : 'Activar'}</span>
            </label>
          </div>

          <div className="form-actions">
            <button type="button" onClick={() => navigate('/cuentas-bancarias')} className="btn-secondary">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CuentaBancariaForm;
